import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ScanSearch, Upload, Loader2, AlertTriangle, MapPin, FileText, ArrowRight, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { LeadGate, LEAD_KEY } from "./TerraaChat";

const SCAN_STEPS = ["Reading document (OCR)…", "Extracting names, survey numbers & dates (NER)…", "Locating the plot…"];

const EntityGroup = ({ title, items }) => {
  const list = items.filter(Boolean);
  if (!list.length) return null;
  return (
    <div>
      <p className="font-heading text-[0.62rem] uppercase tracking-[0.18em] text-gray-400 mb-2">{title}</p>
      <div className="flex flex-wrap gap-2">
        {list.map((v, i) => (
          <span key={i} className="text-xs font-light text-[#113835] bg-[#F4F6F5] border border-[#113835]/10 px-3 py-1.5">{v}</span>
        ))}
      </div>
    </div>
  );
};

const ScanResult = ({ result, onReset }) => {
  const e = result.entities || {};
  const location = (result.location_query || "").trim();
  return (
    <div className="overflow-y-auto h-full px-6 py-6 space-y-7" data-testid="scan-result">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="font-heading text-lg font-medium text-[#113835]" data-testid="scan-doc-type">{result.document_type || "Document"}</p>
          <p className="text-xs text-gray-400 font-light mt-1 flex items-center gap-1.5">
            <FileText size={11} /> {result.scanned_filename} · {result.language || "—"} · confidence: {result.confidence || "—"}
          </p>
        </div>
        <button data-testid="scan-reset-btn" onClick={onReset} className="shrink-0 text-[0.65rem] uppercase tracking-[0.14em] font-heading text-[#1D504C] hover:text-[#C1A74A] flex items-center gap-1.5">
          <RefreshCw size={12} /> Scan Another
        </button>
      </div>

      <p className="text-sm text-gray-600 font-light leading-relaxed border-l-2 border-[#C1A74A]/60 pl-4" data-testid="scan-summary">{result.summary}</p>

      {(result.red_flags || []).length > 0 && (
        <div className="bg-[#fef2f2] border border-[#dc2626]/40 p-5" data-testid="scan-red-flags">
          <p className="font-heading text-xs font-bold uppercase tracking-wide text-[#b91c1c] flex items-center gap-2 mb-3">
            <AlertTriangle size={14} /> Red Flags Detected
          </p>
          <ul className="space-y-1.5">
            {result.red_flags.map((f, i) => (
              <li key={i} className="text-sm text-[#7f1d1d] font-light">• {f}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="space-y-5" data-testid="scan-entities">
        <EntityGroup title="Sellers" items={e.seller_names || []} />
        <EntityGroup title="Buyers" items={e.buyer_names || []} />
        <EntityGroup title="Owners" items={e.owner_names || []} />
        <EntityGroup title="Survey / Plot Numbers" items={[...(e.survey_numbers || []), ...(e.plot_or_cts_numbers || [])]} />
        <EntityGroup title="Location" items={[e.village, e.taluka, e.district, e.state]} />
        <EntityGroup title="Area · Amount · Registration" items={[e.plot_area, e.consideration_amount, e.registration_number]} />
        <EntityGroup title="Dates" items={[e.registration_date, ...(e.other_dates || [])]} />
      </div>

      {location && (
        <div data-testid="scan-map">
          <p className="font-heading text-[0.62rem] uppercase tracking-[0.18em] text-gray-400 mb-2 flex items-center gap-1.5">
            <MapPin size={12} className="text-[#C1A74A]" /> Approximate Plot Location
          </p>
          <iframe
            title="Plot location map"
            data-testid="scan-map-iframe"
            src={`https://www.google.com/maps?q=${encodeURIComponent(location)}&output=embed&z=14`}
            className="w-full h-64 border border-[#113835]/15"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
          <p className="mt-1.5 text-[0.62rem] text-gray-400 font-light">Location approximated from the document text ({location}). Exact plot boundaries require a survey.</p>
        </div>
      )}

      <div className="bg-[#113835] p-6">
        <p className="font-heading text-sm text-white">Want these findings verified against government records?</p>
        <Link to="/request-title-search" data-testid="scan-verify-cta" className="tt-btn-gold mt-4 inline-flex !py-2.5">
          Request Property Verification <ArrowRight size={14} strokeWidth={1.5} />
        </Link>
      </div>
    </div>
  );
};

export const DocumentScanner = () => {
  const [lead, setLead] = useState(() => {
    try { return JSON.parse(localStorage.getItem(LEAD_KEY)) || null; } catch { return null; }
  });
  const [phase, setPhase] = useState("idle");
  const [step, setStep] = useState(0);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => {
    if (phase !== "scanning") return;
    const t = setInterval(() => setStep((s) => Math.min(s + 1, SCAN_STEPS.length - 1)), 4000);
    return () => clearInterval(t);
  }, [phase]);

  const scan = async (file) => {
    if (!file) return;
    setPhase("scanning");
    setStep(0);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data: up } = await api.post("/uploads", form, { headers: { "Content-Type": "multipart/form-data" } });
      const { data } = await api.post("/ai/scan", { lead_id: lead.lead_id, document_id: up.id });
      setResult(data);
      setPhase("done");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Scan failed — please try again");
      setPhase("idle");
    }
  };

  if (!lead) return <LeadGate onReady={setLead} />;

  if (phase === "scanning") {
    return (
      <div className="h-full flex flex-col items-center justify-center px-8 text-center" data-testid="scan-progress">
        <Loader2 size={34} className="animate-spin text-[#C1A74A] mb-8" />
        <p className="font-heading text-base font-medium text-[#113835]">{SCAN_STEPS[step]}</p>
        <p className="mt-3 text-sm text-gray-500 font-light">Marathi & Hindi documents supported. This takes 10–30 seconds.</p>
      </div>
    );
  }

  if (phase === "done" && result) {
    return <ScanResult result={result} onReset={() => { setResult(null); setPhase("idle"); }} />;
  }

  return (
    <div className="h-full flex flex-col items-center justify-center px-8 text-center" data-testid="scan-dropzone">
      <input ref={fileRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" className="hidden"
        onChange={(e) => { scan(e.target.files[0]); e.target.value = ""; }} />
      <div className="w-14 h-14 bg-[#113835] text-[#F4D387] flex items-center justify-center mb-6">
        <ScanSearch size={24} strokeWidth={1.5} />
      </div>
      <p className="font-heading text-lg font-medium text-[#113835]">Scan a property document</p>
      <p className="mt-3 text-sm text-gray-500 font-light max-w-sm leading-relaxed">
        Upload a sale deed, 7/12 extract, EC, or tax receipt (PDF or photo). Terraa AI runs OCR + entity extraction —
        owners, survey numbers, dates, amounts — flags risks, and maps where the plot is.
      </p>
      <button data-testid="scan-upload-btn" onClick={() => fileRef.current?.click()} className="tt-btn-gold mt-8">
        <Upload size={15} strokeWidth={1.5} /> Upload & Scan Document
      </button>
      <p className="mt-4 text-[0.65rem] text-gray-400 font-light">PDF, JPG, PNG, WEBP · up to 10 MB · kept confidential</p>
    </div>
  );
};
